import React, { useRef, useState, useCallback } from 'react';

const matchesPhoto = (file) => Boolean(file?.type && file.type.startsWith('image/'));

const matchesIdentity = (file) => {
  if (!file) return false;
  if (file.type && file.type.startsWith('image/')) return true;
  if (file.type === 'application/pdf') return true;
  return /\.pdf$/i.test(file.name || '');
};

/**
 * Zone glisser-déposer + clic pour joindre un fichier (photo ou pièce d'identité).
 */
const DocumentFileDropZone = ({
  variant = 'photo',
  disabled = false,
  uploading = false,
  onFileSelected,
  onInvalidFile,
  title,
  hint,
}) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const validate = useCallback(
    (file) => {
      if (variant === 'identity') return matchesIdentity(file);
      return matchesPhoto(file);
    },
    [variant]
  );

  const tryAccept = useCallback(
    (file) => {
      if (!file) return;
      if (!validate(file)) {
        onInvalidFile?.(
          variant === 'identity'
            ? 'Veuillez déposer une image (JPG, PNG…) ou un PDF.'
            : 'Veuillez déposer une image (JPG, PNG, WebP…).'
        );
        return;
      }
      onFileSelected(file);
    },
    [onFileSelected, onInvalidFile, validate, variant]
  );

  const openPicker = () => {
    if (disabled || uploading) return;
    inputRef.current?.click();
  };

  const acceptAttr = variant === 'identity' ? 'image/*,application/pdf,.pdf' : 'image/*';

  return (
    <div
      role="button"
      tabIndex={disabled || uploading ? -1 : 0}
      aria-disabled={disabled || uploading}
      aria-busy={uploading}
      onClick={(e) => {
        if (e.target === inputRef.current) return;
        openPicker();
      }}
      onKeyDown={(e) => {
        if (disabled || uploading) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openPicker();
        }
      }}
      onDragEnter={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled && !uploading) setDragOver(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled && !uploading) setDragOver(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        if (disabled || uploading) return;
        const file = e.dataTransfer.files?.[0];
        tryAccept(file);
      }}
      className={[
        'relative flex min-h-[7.5rem] flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-5 text-center transition outline-none focus-visible:ring-2 focus-visible:ring-entrea-royal focus-visible:ring-offset-2',
        dragOver && !disabled && !uploading
          ? 'border-entrea-royal bg-entrea-panel/60 scale-[1.01]'
          : 'border-gray-300 bg-gray-50/80 hover:border-entrea-sky hover:bg-entrea-panel/30',
        disabled || uploading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      ].join(' ')}
    >
      <input
        ref={inputRef}
        type="file"
        accept={acceptAttr}
        className="sr-only"
        tabIndex={-1}
        disabled={disabled || uploading}
        onChange={(ev) => {
          const file = ev.target.files?.[0];
          ev.target.value = '';
          tryAccept(file);
        }}
        onClick={(e) => e.stopPropagation()}
      />

      {uploading ? (
        <div className="flex flex-col items-center gap-2 text-sm text-entrea-royal">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-entrea-royal border-t-transparent" />
          <span>Téléversement…</span>
        </div>
      ) : (
        <>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-gray-200">
            <svg className="h-5 w-5 text-entrea-royal" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </div>
          <p className="text-sm font-semibold text-gray-900">{title}</p>
          <p className="mt-1 max-w-[20rem] text-xs text-gray-600">
            Glissez-déposez un fichier ici, ou{' '}
            <span className="font-medium text-entrea-royal underline decoration-entrea-sky/60 underline-offset-2">
              parcourir
            </span>{' '}
            pour joindre un fichier.
          </p>
          {hint ? <p className="mt-2 text-[11px] text-gray-500">{hint}</p> : null}
        </>
      )}
    </div>
  );
};

export default DocumentFileDropZone;
